/**
 * CustomContact — the party admin / candidate contact book (api.core, MongoDB-backed).
 *
 * ⚠️ THIS CONTROLLER DOES NOT USE THE CORE RESPONSE ENVELOPE.
 * Every sibling file in this directory types responses as `IApiResponse<T>` (the
 * `{ isSuccessful, statusCode, message, data }` wrapper). CustomContact returns the DTO **bare**,
 * exactly like the `Legal` controller. Read `response` directly — a copy-pasted `.data.data` here
 * silently yields `undefined`, not an error.
 *
 * Other contract facts worth knowing before editing (all verified against api.core):
 *  - Write and read field names DIFFER: you POST `phoneNumber`/`lga`/`ward`/`pu` and read back
 *    `phoneE164`/`lgaCode`/`wardCode`/`puCode`. See toContactInput/fromContactDto in the engine.
 *  - There is NO single-contact POST. Creating one contact means posting a 1-item batch.
 *  - `POST /contacts/batch` returns **201 even when it created nothing**. Success must be read from
 *    `createdCount`, never from the status code.
 *  - Writes are NOT consent-gated. Batch used to 403 with `{ code: 'CONSENT_REQUIRED' }` pending an
 *    accepted `LegalDocumentType = 5`; that gate was removed backend-side (CustomContactConstants.cs
 *    is now a tombstone) and consent is collected once at login. Do not reintroduce a consent branch.
 *  - `PUT /contacts/{id}` is a FULL REPLACE, not a patch. Omitted fields are nulled. It takes
 *    `ContactInputDto`, which has no group field, so an edit can never disturb list membership.
 *  - Contacts are scoped by the JWT `UserId`, not by party, and a read silently merges contacts
 *    from every owner who allow-listed your email — with no owner field on the row.
 */

import { PAGE_SIZE } from '@/constants/global';

import { coreApiSlice } from '../apiSlice';

// ── Wire types ───────────────────────────────────────────────

/** WRITE shape. Every field is a nullable string server-side with no validation attributes. */
export interface IContactInputDto {
	firstName?: string;
	lastName?: string;
	phoneNumber?: string;
	email?: string;
	gender?: string;
	lga?: string;
	ward?: string;
	pu?: string;
}

/** READ shape. Note the field names differ from IContactInputDto. */
export interface IContactDto {
	id: string;
	firstName: string | null;
	lastName: string | null;
	phoneE164: string | null;
	email: string | null;
	gender: string | null;
	lgaCode: string | null;
	wardCode: string | null;
	puCode: string | null;
	/**
	 * Lists this contact belongs to.
	 *
	 * ⚠️ MAY CONTAIN IDS YOU CANNOT RESOLVE. A read merges contacts from every owner who allow-listed
	 * you, and their `groupIds` point at THEIR lists — which `GET /groups` (yours only) will never
	 * return. Resolve through `resolveContactGroups`, which counts the strangers rather than
	 * rendering a chip whose name we do not have.
	 */
	groupIds: string[] | null;
	createdAt: string;
	updatedAt: string;
}

/** Bare paged envelope — note `items`/`total`, NOT `data`/`totalCount`. */
export interface IContactPage {
	items: IContactDto[];
	pageNumber: number;
	pageSize: number;
	total: number;
}

export interface IContactBatchResult {
	totalSubmitted: number;
	createdCount: number;
	duplicateCount: number;
	invalidCount: number;
	created: IContactDto[];
	/** `index` is 0-based into the `contacts` array YOU submitted for this request. */
	rejected: { index: number; reason: string }[];

	/**
	 * Pre-existing contacts that a rejected-as-duplicate row caused to be filed into `groupId`.
	 *
	 * Counts contacts that were already members, so it is "how many of your existing contacts this
	 * import touched", not "how many memberships were newly created". Without it, "12 saved · 4,988
	 * already existed" reads as 4,988 wasted rows when tagging them was the entire point.
	 * Always 0 when no `groupId` was sent or the group could not be applied.
	 */
	groupedExistingCount?: number;

	/**
	 * Whether the `groupId` we sent was honoured.
	 *
	 * ⚠️ ABSENT IS NOT `false`. `false` means the server understood the field and refused it — read
	 * `groupError`. `undefined` means the server predates the field, never saw it, and created every
	 * contact ungrouped with nothing anywhere to notice. Reading `undefined` as `false` cries wolf;
	 * reading it as `true` is the silent version of the bug this field exists to prevent.
	 * Always read it through `readBatchGrouping`, never with a bare truthiness check.
	 */
	groupApplied?: boolean;

	/**
	 * `INVALID_GROUP_ID | GROUP_NOT_FOUND | GROUP_FORBIDDEN`.
	 *
	 * Typed as a bare `string`, like `IDuplicateCheckRowDto.issues`: an unrecognised code must be
	 * shown to the user verbatim, not swallowed by an exhaustive union that has fallen behind.
	 */
	groupError?: string;
}

export interface IContactAccess {
	id: string;
	ownerUserId: number;
	ownerEmail: string;
	userAccess: string[];
}

/** A named list. Owner-scoped, static membership, name unique per owner (case-insensitive). */
export interface IContactGroupDto {
	id: string;
	name: string;
	description: string | null;
	/** Server-computed by aggregation, not a stored counter — it cannot drift out of date. */
	memberCount: number;
	createdAt: string;
	updatedAt: string;
}

export interface IContactGroupInput {
	name: string;
	description?: string;
}

export interface IContactMergeRequest {
	primaryId: string;
	duplicateIds: string[];
	/**
	 * The surviving contact's field values. ALWAYS send this — see `planMerge`.
	 *
	 * Omitting it makes the server backfill the survivor's NULL fields only (`??=`), while its
	 * completeness ranking treats whitespace as empty. A survivor whose `firstName` is `''` is
	 * therefore scored incomplete AND never filled — a merge that looks like it will recover the
	 * name silently does not.
	 */
	fields?: IContactInputDto;
}

export interface IDuplicateCheckRowDto {
	/** 0-based into the array YOU submitted for this request. */
	index: number;
	isValid: boolean;
	isDuplicate: boolean;
	canImport: boolean;
	phoneE164: string | null;
	email: string | null;
	/** Human-readable strings, deliberately not an enum (the host serialises enums as ints). */
	issues: string[] | null;
	/** Points at EARLIER rows only, 0-based into what you submitted. */
	conflictsWithRowIndexes: number[] | null;
	/** Ids of already-saved contacts this row clashes with. Owner-scoped, so always yours. */
	conflictsWithContactIds: string[] | null;
}

export interface IDuplicateCheckResultDto {
	totalSubmitted: number;
	importableCount: number;
	invalidCount: number;
	duplicateCount: number;
	readyToImport: boolean;
	rows: IDuplicateCheckRowDto[] | null;
}

export interface IDuplicateClusterDto {
	/** 'Phone' | 'Email' | 'Name', cluster-level. Normalise before switching on it. */
	matchReasons: string[] | null;
	suggestedPrimaryId: string;
	contacts: IContactDto[] | null;
}

export interface IDuplicateScanDto {
	totalScanned: number;
	clusterCount: number;
	duplicateContactCount: number;
	/** TRUE means the scan stopped at `scanLimit`. Zero clusters + truncated is NOT a clean book. */
	truncated: boolean;
	scanLimit: number;
	clusters: IDuplicateClusterDto[] | null;
}

export interface IContactListParams {
	Search?: string;
	Gender?: string;
	Lga?: string;
	Ward?: string;
	Pu?: string;
	/** Filter to one list. The wire name is `GroupId`; everything user-facing says "list". */
	GroupId?: string;
	PageNumber?: number;
	PageSize?: number;
	/**
	 * Only the literal 'name' does anything; every other value silently means createdAt-desc.
	 * Typed as a union so no arbitrary string can reach the wire.
	 * NOTE: name-sort has no tiebreaker server-side, so paginating it can duplicate AND DROP rows.
	 * Do not expose it in the UI until the backend adds an `_id` tiebreaker.
	 */
	Sort?: 'name';
}

// ── Guards ───────────────────────────────────────────────────

const BASE = '/CustomContact';

/**
 * The reserved URL segment for the whole contact book (`/admin/contacts/all`).
 *
 * It lives HERE, in the wire layer, because "this string is never a `GroupId`" is a wire-contract
 * fact: `GroupId=all` matches no group, and on a write a bad group id is a SOFT failure (201, the
 * contacts created ungrouped, `groupApplied: false`) — so sending it would not even error, it would
 * quietly file nothing. Both the list request adapter and `buildBatchRequest` drop it here so no
 * call site can forget. `contacts.config.ts` re-exports it for the app layer.
 *
 * Group ids are 24-char ObjectIds so a collision is impossible, but no code should lean on that:
 * branch on `isImplicitList`, not on the shape of the string.
 */
export const IMPLICIT_LIST_ID = 'all';

/**
 * Clamp the page size before it reaches the wire.
 *
 * Unlike the other 33 paged DTOs in api.core (which clamp to 100), CustomContactService does
 * `PageSize is < 1 or > 100 ? 20 : PageSize` — so an out-of-range value RESETS TO 20 rather than
 * clamping. Passing `useExport`'s EXPORT_PAGE_SIZE of 500 would therefore return 20 rows per page
 * while the caller computed totalPages from 500, silently downloading ~4% of the data with no error.
 */
export const safeContactPageSize = (n?: number): number => (n && n >= 1 && n <= 100 ? n : PAGE_SIZE);

/** The maximum rows a single contacts request can return. There is no bulk-fetch endpoint. */
export const CONTACTS_MAX_PAGE_SIZE = 100;

// ── Errors ───────────────────────────────────────────────────

export type ContactErrorKind =
	| 'not-owner'
	| 'duplicate-phone'
	| 'duplicate-email'
	| 'duplicate'
	| 'name-taken'
	| 'invalid-name'
	| 'invalid-merge'
	| 'invalid'
	| 'gone'
	| 'generic';

/**
 * Which field a 409 is actually about.
 *
 * Phone AND email are each unique per owner, so a conflict can be either. Exported and pinned
 * because the contact form sets a field-level error from this — and pointing the user at the wrong
 * input ("this number is already in your contacts" under a phone they have never used before) is
 * worse than a generic toast, because it sends them off to fix something that is not broken.
 */
export const duplicateFieldOf = (code?: string, message?: string): 'phone' | 'email' | null => {
	const haystack = `${code ?? ''} ${message ?? ''}`.toLowerCase();
	if (haystack.includes('email')) return 'email';
	if (haystack.includes('phone') || haystack.includes('number')) return 'phone';
	return null;
};

/**
 * Map an RTK Query error onto something the UI can act on.
 *
 * The consent 403 is gone (the backend gate was removed), so the only 403 this controller still
 * raises is ASP.NET `Forbid()` with an EMPTY body, meaning "this row belongs to someone who shared
 * it with you". Since `ContactDto` carries no owner field, that 403 is the ONLY way ownership is
 * discoverable — which is why it gets its own kind rather than falling through to `generic`.
 */
export const contactApiError = (err: unknown, fallback = 'Something went wrong'): { kind: ContactErrorKind; message: string } => {
	const status = (err as { status?: number | string })?.status;
	const body = (err as { data?: unknown })?.data;
	const code = typeof body === 'object' && body !== null ? (body as { code?: string }).code : undefined;
	const serverMessage = typeof body === 'object' && body !== null ? (body as { message?: string }).message : undefined;

	if (status === 403) {
		// A 403 that carries a `code` is something the backend added after this was written. Show its
		// own message rather than mislabelling it as an ownership problem the user cannot act on.
		if (code) return { kind: 'generic', message: serverMessage || fallback };
		return { kind: 'not-owner', message: 'You can only edit contacts you own. This one was shared with you by another user.' };
	}
	if (status === 409) {
		if (code === 'NAME_TAKEN') {
			return { kind: 'name-taken', message: serverMessage || 'You already have a list with that name.' };
		}
		const field = duplicateFieldOf(code, serverMessage);
		if (field === 'email') {
			return { kind: 'duplicate-email', message: serverMessage || 'This email is already in your contacts.' };
		}
		if (field === 'phone') {
			return { kind: 'duplicate-phone', message: serverMessage || 'This number is already in your contacts.' };
		}
		return { kind: 'duplicate', message: serverMessage || 'This contact is already in your contacts.' };
	}
	if (status === 400) {
		if (code === 'INVALID_NAME') {
			return { kind: 'invalid-name', message: serverMessage || 'Give the list a name.' };
		}
		if (code === 'INVALID_MERGE') {
			return { kind: 'invalid-merge', message: serverMessage || 'These contacts cannot be merged as chosen.' };
		}
		if (code === 'EMPTY_BATCH') {
			return { kind: 'invalid', message: serverMessage || 'There was nothing to save.' };
		}
		return { kind: 'invalid', message: serverMessage || 'Some details are missing or invalid.' };
	}
	if (status === 404) {
		// NOT "that contact no longer exists". GET /contacts/{id} answers 404 *deliberately* for a
		// contact you are not allowed to read, so that callers cannot probe for ids. Asserting a
		// deletion here would state something we do not know — and the row may be perfectly alive in
		// its owner's book.
		return {
			kind: 'gone',
			message: "That contact isn't available. It may have been deleted, or shared with you by someone who has since stopped.",
		};
	}
	return { kind: 'generic', message: serverMessage || fallback };
};

// ── Request builders ─────────────────────────────────────────
//
// Every trap in this contract is a REQUEST-SHAPE trap: a body that must ride on a DELETE, a PageSize
// that resets rather than clamps, a wire key that differs from the model key, a field whose absence
// changes what the server writes. RTK Query does not expose an endpoint's `query` function on
// `api.endpoints.X`, so none of that is reachable from a test while it lives inline.
//
// Extracting the shapes as pure functions makes each one directly assertable, and keeps the same
// "pure core, thin wire" split the module's engine.ts already uses.

export const buildContactListRequest = (params: IContactListParams) => ({
	url: `${BASE}/contacts`,
	params: { ...params, PageSize: safeContactPageSize(params.PageSize) },
});

/**
 * Add or remove list members.
 *
 * ⚠️ THE DELETE CARRIES A REQUEST BODY. `fetchBaseQuery` serialises `body` for every method except
 * GET/HEAD, so this works — but an axios `client.delete(url, payload)` would pass the payload as
 * *config*, sending no `contactIds` at all. The server answers that with 200 and an unchanged
 * memberCount, so the bug presents as "remove silently does nothing", with no error anywhere.
 * Never route membership through src/lib/api-client.ts.
 */
export const buildGroupMembershipRequest = (method: 'POST' | 'DELETE', args: { groupId: string; contactIds: string[] }) => ({
	url: `${BASE}/groups/${args.groupId}/contacts`,
	method,
	body: { contactIds: args.contactIds },
});

/**
 * The only create path — a single add is a 1-item batch.
 *
 * The body is built key-by-key rather than spread, so `userAccess` cannot ride along from a caller's
 * object. The endpoint accepts that field and treats any non-null value as a wholesale REPLACE of
 * the sharing allowlist, which means an accidental `[]` silently revokes everyone you share with.
 * `groupId` is therefore an explicit NAMED parameter rather than a widened body — the same rule,
 * one field later. Never change this to a spread.
 *
 * `groupId` files every contact the call creates into an existing list, and a row rejected as a
 * duplicate also files the EXISTING contact it collided with (`groupedExistingCount`) — which is
 * what makes re-posting a spreadsheet a supported way to tag a cohort that is already saved.
 *
 * `IMPLICIT_LIST_ID` is dropped HERE rather than at the call sites. It is a reserved URL segment,
 * not a group id, and a bad group id is a SOFT failure — 201, contacts created ungrouped,
 * `groupApplied: false` — so sending it would not even raise an error, the whole sitting would
 * quietly land nowhere. One guard at the wire beats N guards no one can be relied on to write.
 *
 * The key is built conditionally rather than as `groupId: x || undefined`. `JSON.stringify` would
 * drop an undefined value anyway, but the request-shape tests assert on `Object.keys(body)`, and
 * that assertion only means something if the key is genuinely absent.
 */
export const buildBatchRequest = (contacts: IContactInputDto[], groupId?: string) => {
	const wire = (groupId ?? '').trim();
	const filed = wire !== '' && wire !== IMPLICIT_LIST_ID;
	return {
		url: `${BASE}/contacts/batch`,
		method: 'POST' as const,
		body: filed ? { contacts, groupId: wire } : { contacts },
	};
};

export const buildMergeRequest = (body: IContactMergeRequest) => ({
	url: `${BASE}/contacts/merge`,
	method: 'POST' as const,
	body,
});

/** Vets rows before they are saved. Writes nothing — see the endpoint's invalidatesTags. */
export const buildCheckDuplicatesRequest = (contacts: IContactInputDto[]) => ({
	url: `${BASE}/contacts/check-duplicates`,
	method: 'POST' as const,
	// Wrapped in `{ contacts }`, same as batch — verified against the live CheckDuplicatesRequest schema.
	body: { contacts },
});

// ── Endpoints ────────────────────────────────────────────────

// Tag types are added HERE rather than on coreApiSlice. That slice declares only its own two
// (`ElectionObservers`, `ObserverManagement`) and backs 30+ api files, so widening it would change
// cache behaviour app-wide. enhanceEndpoints scopes these tags to this module with zero blast radius.
const contactApi = coreApiSlice.enhanceEndpoints({
	addTagTypes: ['CustomContact', 'CustomContactAccess', 'ContactGroup'],
});

export const customContactApiSlice = contactApi.injectEndpoints({
	endpoints: (builder) => ({
		getContacts: builder.query<IContactPage, IContactListParams>({
			query: buildContactListRequest,
			providesTags: (result) =>
				result
					? [
							...result.items.map((c) => ({ type: 'CustomContact' as const, id: c.id })),
							{ type: 'CustomContact' as const, id: 'LIST' },
						]
					: [{ type: 'CustomContact' as const, id: 'LIST' }],
		}),

		getContactById: builder.query<IContactDto, string>({
			query: (id) => ({ url: `${BASE}/contacts/${id}` }),
			providesTags: (_r, _e, id) => [{ type: 'CustomContact', id }],
		}),

		/**
		 * The only create path — a single add is a 1-item batch.
		 *
		 * `userAccess` is deliberately ABSENT from this request type. The endpoint accepts it and
		 * treats a non-null value as a wholesale REPLACE of the sharing allowlist, so sending `[]`
		 * silently revokes everyone. A type that has no such field cannot make that mistake; a
		 * convention ("remember not to send it") would rot.
		 */
		createContactsBatch: builder.mutation<IContactBatchResult, { contacts: IContactInputDto[]; groupId?: string }>({
			query: ({ contacts, groupId }) => buildBatchRequest(contacts, groupId),
			// A batch that carried a groupId is TWO facts, exactly like addContactsToGroup: the list's
			// memberCount moved, and every contact it created or collided with now carries the id in
			// groupIds[]. Invalidating only CustomContact leaves a stale count on the shelf — and that
			// count is what prices a send.
			//
			// Fired whenever an id was SENT, not only when the server reports it applied: getContactGroups
			// is one unpaged array, so a redundant refetch costs one request and cannot be wrong, whereas
			// a missed one leaves a wrong number on screen.
			invalidatesTags: (_r, _e, { groupId }) => [
				{ type: 'CustomContact', id: 'LIST' },
				...(groupId
					? [
							{ type: 'ContactGroup' as const, id: groupId },
							{ type: 'ContactGroup' as const, id: 'LIST' },
						]
					: []),
			],
		}),

		/** Full replace — always send every field, or the omitted ones are nulled. */
		updateContact: builder.mutation<IContactDto, { id: string; body: IContactInputDto }>({
			query: ({ id, body }) => ({ url: `${BASE}/contacts/${id}`, method: 'PUT', body }),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'CustomContact', id },
				{ type: 'CustomContact', id: 'LIST' },
			],
		}),

		// 204 No Content — RTK surfaces that as `undefined`. (`void` here is the repo's older habit
		// but trips @typescript-eslint/no-invalid-void-type.)
		deleteContact: builder.mutation<undefined, string>({
			query: (id) => ({ url: `${BASE}/contacts/${id}`, method: 'DELETE' }),
			// memberCount is an aggregation over contacts, so deleting one changes the count of every
			// list it belonged to. Without the group tag the shelf keeps showing the old figure.
			invalidatesTags: [
				{ type: 'CustomContact', id: 'LIST' },
				{ type: 'ContactGroup', id: 'LIST' },
			],
		}),

		getContactAccess: builder.query<IContactAccess, undefined>({
			query: () => ({ url: `${BASE}/access` }),
			providesTags: ['CustomContactAccess'],
		}),

		/** Full replace of the allowlist. Always send the complete intended array. */
		updateContactAccess: builder.mutation<IContactAccess, { userAccess: string[] }>({
			query: (body) => ({ url: `${BASE}/access`, method: 'PUT', body }),
			// Changing who shares with you changes which contacts a read returns, so the list is stale too.
			invalidatesTags: ['CustomContactAccess', { type: 'CustomContact', id: 'LIST' }],
		}),

		// ── Lists (wire name: groups) ────────────────────────────

		/** Returns YOUR lists only — never a bare array wrapped in an envelope, and never paged. */
		getContactGroups: builder.query<IContactGroupDto[], undefined>({
			query: () => ({ url: `${BASE}/groups` }),
			providesTags: (result) =>
				result
					? [...result.map((g) => ({ type: 'ContactGroup' as const, id: g.id })), { type: 'ContactGroup' as const, id: 'LIST' }]
					: [{ type: 'ContactGroup' as const, id: 'LIST' }],
		}),

		getContactGroupById: builder.query<IContactGroupDto, string>({
			// Metadata + memberCount only — there are no members in this response. List them with
			// getContacts({ GroupId }).
			query: (id) => ({ url: `${BASE}/groups/${id}` }),
			providesTags: (_r, _e, id) => [{ type: 'ContactGroup', id }],
		}),

		createContactGroup: builder.mutation<IContactGroupDto, IContactGroupInput>({
			query: (body) => ({ url: `${BASE}/groups`, method: 'POST', body }),
			invalidatesTags: [{ type: 'ContactGroup', id: 'LIST' }],
		}),

		updateContactGroup: builder.mutation<IContactGroupDto, { id: string; body: IContactGroupInput }>({
			query: ({ id, body }) => ({ url: `${BASE}/groups/${id}`, method: 'PUT', body }),
			invalidatesTags: (_r, _e, { id }) => [
				{ type: 'ContactGroup', id },
				{ type: 'ContactGroup', id: 'LIST' },
			],
		}),

		deleteContactGroup: builder.mutation<undefined, string>({
			query: (id) => ({ url: `${BASE}/groups/${id}`, method: 'DELETE' }),
			// Deleting a list KEEPS its contacts and $pulls the id from every member, so every visible
			// row's groupIds just changed — and a page currently filtered by this GroupId is now
			// meaningless. Invalidating only the group tags would leave stale list chips on the table.
			invalidatesTags: (_r, _e, id) => [
				{ type: 'ContactGroup', id },
				{ type: 'ContactGroup', id: 'LIST' },
				{ type: 'CustomContact', id: 'LIST' },
			],
		}),

		/**
		 * Add members. Idempotent ($addToSet), and gives NO per-id feedback — unknown, malformed and
		 * not-yours ids are all silently ignored, so the response cannot tell you what landed.
		 * Callers must diff memberCount themselves; see use-list-membership.
		 */
		addContactsToGroup: builder.mutation<IContactGroupDto, { groupId: string; contactIds: string[] }>({
			query: (args) => buildGroupMembershipRequest('POST', args),
			// A membership change is TWO facts: the group's memberCount and each contact's groupIds.
			// Invalidating only ContactGroup leaves stale chips on the table; only CustomContact leaves
			// a stale count on the shelf. The per-id contact tags come from the request args, so this
			// is exact rather than a blanket refetch.
			invalidatesTags: (_r, _e, { groupId, contactIds }) => [
				{ type: 'ContactGroup', id: groupId },
				{ type: 'ContactGroup', id: 'LIST' },
				{ type: 'CustomContact', id: 'LIST' },
				...contactIds.map((id) => ({ type: 'CustomContact' as const, id })),
			],
		}),

		/** Remove members. ⚠️ Carries a request body — see buildGroupMembershipRequest. */
		removeContactsFromGroup: builder.mutation<IContactGroupDto, { groupId: string; contactIds: string[] }>({
			query: (args) => buildGroupMembershipRequest('DELETE', args),
			invalidatesTags: (_r, _e, { groupId, contactIds }) => [
				{ type: 'ContactGroup', id: groupId },
				{ type: 'ContactGroup', id: 'LIST' },
				{ type: 'CustomContact', id: 'LIST' },
				...contactIds.map((id) => ({ type: 'CustomContact' as const, id })),
			],
		}),

		// ── Duplicates ───────────────────────────────────────────

		/**
		 * Pre-flight vet of rows you are ABOUT to import. Writes nothing.
		 *
		 * `invalidatesTags: []` is deliberate and must stay. The instinct on reading this is to
		 * "helpfully" add `{CustomContact, LIST}` — which would turn a preflight into a full page
		 * refetch on every re-check of an import review screen.
		 */
		checkContactDuplicates: builder.mutation<IDuplicateCheckResultDto, IContactInputDto[]>({
			query: buildCheckDuplicatesRequest,
			invalidatesTags: [],
		}),

		/**
		 * Scan the SAVED book for merge-ready clusters.
		 *
		 * ⚠️ A MUTATION, NOT A QUERY — and a GET modelled as a mutation looks like a mistake, so:
		 * this is a full, unindexed collection scan (`OwnerUserId` has no Mongo index anywhere in
		 * api.core) capped at 20,000 documents. It must fire exactly when a human asks and never
		 * otherwise. As a `query` providing `CustomContact`, editing one contact with this screen open
		 * would re-run the whole 20k scan; as a tag-less `query`, `keepUnusedDataFor` would serve a
		 * PRE-MERGE result from cache, and merging a stale cluster 404s the entire call because every
		 * duplicate id must still exist. A mutation is structurally immune to both, and it forces the
		 * caller to hold `scannedAt` — which is what honest UI needs anyway.
		 */
		scanContactDuplicates: builder.mutation<IDuplicateScanDto, undefined>({
			query: () => ({ url: `${BASE}/contacts/duplicates` }),
			invalidatesTags: [],
		}),

		/** Folds duplicates into `primaryId` and HARD-DELETES them. No undo, no audit row. */
		mergeContacts: builder.mutation<IContactDto, IContactMergeRequest>({
			query: buildMergeRequest,
			// The survivor inherits the union of every member's lists, and which lists those are is
			// only knowable from the response. Foreign ids in there invalidate a tag nothing provides,
			// which is a harmless no-op.
			invalidatesTags: (result, _e, { primaryId, duplicateIds }) => [
				{ type: 'CustomContact', id: 'LIST' },
				{ type: 'CustomContact', id: primaryId },
				...duplicateIds.map((id) => ({ type: 'CustomContact' as const, id })),
				{ type: 'ContactGroup', id: 'LIST' },
				...(result?.groupIds ?? []).map((id) => ({ type: 'ContactGroup' as const, id })),
			],
		}),
	}),
});

export const {
	useGetContactsQuery,
	useLazyGetContactsQuery,
	useGetContactByIdQuery,
	useLazyGetContactByIdQuery,
	useCreateContactsBatchMutation,
	useUpdateContactMutation,
	useDeleteContactMutation,
	useGetContactAccessQuery,
	useUpdateContactAccessMutation,
	useGetContactGroupsQuery,
	useLazyGetContactGroupsQuery,
	useGetContactGroupByIdQuery,
	useLazyGetContactGroupByIdQuery,
	useCreateContactGroupMutation,
	useUpdateContactGroupMutation,
	useDeleteContactGroupMutation,
	useAddContactsToGroupMutation,
	useRemoveContactsFromGroupMutation,
	useCheckContactDuplicatesMutation,
	useScanContactDuplicatesMutation,
	useMergeContactsMutation,
} = customContactApiSlice;
